/**
 * Mode-aware bookable windows for custom-slot re-validation.
 *
 * When the recruiter switches interview type (1-on-1 ↔ panel) the
 * availability rule flips between union and intersection. Auto-fill slots
 * re-derive from the fresh pipeline output automatically, but manually
 * clicked `customPicks` are kept verbatim — so a slot placed in 1-on-1
 * (valid because ONE interviewer was free, sitting over another's busy or
 * on a day with no panel overlap) survives into panel as an off-day /
 * over-busy slot. This helper exposes the same free windows the generator
 * uses so the reducer can drop only the customs that no longer fit.
 */
import moment from 'moment-timezone';
import type { BusyEvent, InterviewKind, Interval } from '../../../availability';
import { computeRawGaps } from './pipeline';

export interface FreeWindowsArgs {
  availability: Interval[];
  busyEvents: BusyEvent[];
  duration: number;
  timezone: string;
  scheduleByUserId?: Record<string, Interval[]>;
  interviewKind?: InterviewKind;
  nowMs?: number;
  /**
   * Shortest window worth keeping, in minutes. Defaults to `duration`,
   * which is what slot re-validation wants — a window too short to hold
   * an interview can never contain a custom pick.
   *
   * Pass `1` when the caller needs to know whether ANY free time exists
   * rather than whether a bookable slot fits: at the duration default a
   * day whose only remaining gap is shorter than the interview comes
   * back indistinguishable from a day the calendar has completely
   * filled (ENG-3642).
   */
  minSlotMinutes?: number;
}

export interface FreeWindowsResult {
  /** Mode-aware bookable windows (`availability − busy`), grouped by day. */
  windowsByDay: Record<string, Interval[]>;
  /**
   * Days whose working-hours data is loaded — kind-independent, derived
   * from `scheduleByUserId`. Re-validation only drops customs on days we
   * actually have data for; a custom on an unloaded (far-future) day is
   * preserved because we can't yet say whether it's valid.
   */
  loadedDays: Set<string>;
}

function addSpannedDays(
  iv: { start: string; end: string },
  timezone: string,
  out: Set<string>
): void {
  const start = moment.tz(iv.start, timezone);
  const end = moment.tz(iv.end, timezone);
  if (!start.isValid() || !end.isValid()) return;
  const cursor = start.clone().startOf('day');
  const lastDay = end.clone().startOf('day');
  // Exclusive end exactly at midnight belongs to the previous day.
  const stopBefore = end.isSame(lastDay)
    ? lastDay
    : lastDay.clone().add(1, 'day');
  while (cursor.isBefore(stopBefore)) {
    out.add(cursor.format('YYYY-MM-DD'));
    cursor.add(1, 'day');
  }
}

export function computeFreeWindowsByDay({
  availability,
  busyEvents,
  duration,
  timezone,
  scheduleByUserId,
  interviewKind,
  nowMs,
  minSlotMinutes,
}: FreeWindowsArgs): FreeWindowsResult {
  const gaps = computeRawGaps({
    availability,
    busyEvents,
    duration,
    timezone,
    scheduleByUserId,
    interviewKind,
    nowMs,
    minSlotMinutes,
  });
  const windowsByDay: Record<string, Interval[]> = {};
  for (const g of gaps) {
    const key = moment.tz(g.start, timezone).format('YYYY-MM-DD');
    if (!windowsByDay[key]) windowsByDay[key] = [];
    windowsByDay[key].push({ start: g.start, end: g.end });
  }

  const loadedDays = new Set<string>();
  if (scheduleByUserId) {
    for (const intervals of Object.values(scheduleByUserId)) {
      for (const iv of intervals ?? [])
        addSpannedDays(iv, timezone, loadedDays);
    }
  }

  return { windowsByDay, loadedDays };
}
